// Conversores puros entre FormDTO/FolderDTO (backend) e FormDocument/Folder
// (tipagem interna do builder). Substitui o antigo `formStorage.ts` adapter
// que era 240 LOC, sendo metade thin wrappers que apenas chamavam formsApi.
//
// 2026-05-18: cleanup arquitetural. Cada caller agora chama formsApi/foldersApi
// direto + usa o converter aqui pra mapear shape. Sem nada async neste arquivo —
// é puro tipo/data conversion.

import type {
  FormDocument,
  FormListItem,
  Folder,
  FormField,
  FormSettings,
  FormTheme,
} from '@nanoapp/types';
import { v4 as uuid } from 'uuid';
import { DEFAULT_THEME } from '../constants/themes';
import type { FormDTO, FolderDTO } from '../lib/api';

// ─── DTO → Internal ─────────────────────────────────────────────

/** Converte FormDTO (backend) → FormDocument (uso interno builder). */
export function toFormDocument(dto: FormDTO): FormDocument {
  const settings = (dto.settings as FormSettings | null | undefined) ?? ({} as Partial<FormSettings>);
  // Garante shape mínimo. settings.title pode estar ausente em forms criados
  // antes do adapter — usamos `name` como fallback.
  const safeSettings: FormSettings = {
    title: settings.title ?? dto.name ?? 'Formulário sem título',
    description: settings.description ?? dto.description ?? '',
    titleAlign: settings.titleAlign,
    successMessage: settings.successMessage ?? 'Obrigado pelo envio!',
    redirectUrl: settings.redirectUrl ?? '',
    allowResubmit: settings.allowResubmit,
    resubmitButtonLabel: settings.resubmitButtonLabel,
    submitButtonLabel: settings.submitButtonLabel ?? 'Enviar',
    submitButtonIcon: settings.submitButtonIcon,
    submitButtonAlign: settings.submitButtonAlign,
    submitButtonEnabled: settings.submitButtonEnabled,
    webhook: settings.webhook ?? {
      url: '', method: 'POST', headers: [], enabled: false, corsMode: 'no-cors',
      events: { onOpen: false, onSubmit: true, onFieldChange: [] },
    },
    header: settings.header ?? { enabled: false, title: '', description: '', imageUrl: '' },
    customHead: settings.customHead ?? '',
    customBody: settings.customBody ?? '',
    paginated: settings.paginated ?? false,
    offlineStorage: settings.offlineStorage,
    offlineSync: settings.offlineSync,
    formAuth: settings.formAuth,
    successButtons: settings.successButtons,
    footer: settings.footer,
    collectionTarget: settings.collectionTarget,
  };

  const theme = (dto.theme as FormTheme | null | undefined);
  const safeTheme: FormTheme = theme && Object.keys(theme).length > 0
    ? { ...DEFAULT_THEME, ...theme }
    : { ...DEFAULT_THEME };

  return {
    id: dto.id,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    settings: safeSettings,
    theme: safeTheme,
    fields: ((dto.schema as FormField[] | null | undefined) ?? []) as FormField[],
    folderId: dto.folder_id ?? undefined,
    // 2026-05-18: tag de cliente (Workspace).
    customerId: dto.customer_id ?? null,
  };
}

/** Converte FormDTO → FormListItem (lista compacta de forms). */
export function toFormListItem(dto: FormDTO): FormListItem {
  const settings = (dto.settings as Partial<FormSettings> | null | undefined) ?? {};
  const fields = ((dto.schema as FormField[] | null | undefined) ?? []) as FormField[];
  return {
    id: dto.id,
    title: settings.title ?? dto.name ?? 'Formulário sem título',
    fieldCount: fields.length,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    folderId: dto.folder_id ?? undefined,
    // 2026-05-18: customer/app tags pra filter + chip na lista.
    customerId: dto.customer_id ?? null,
    appId: dto.app_id ?? null,
  };
}

/** Converte FolderDTO → Folder (uso interno builder). */
export function toFolder(dto: FolderDTO): Folder {
  return {
    id: dto.id,
    title: dto.title,
    description: dto.description ?? '',
    color: dto.color,
    active: dto.active,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
  };
}

// ─── Internal → API payload (helpers de save) ────────────────────

/** Constrói payload pra formsApi.create / formsApi.update a partir de FormDocument. */
export function formDocumentToPayload(doc: FormDocument): {
  name: string;
  description: string;
  schema: FormField[];
  settings: FormSettings;
  theme: FormTheme;
  folder_id: string | null;
  customer_id: string | null;
} {
  return {
    name: doc.settings.title || 'Formulário sem título',
    description: doc.settings.description ?? '',
    schema: doc.fields,
    settings: doc.settings,
    theme: doc.theme,
    folder_id: doc.folderId ?? null,
    // 2026-05-18: tag de cliente persistida no PATCH.
    customer_id: doc.customerId ?? null,
  };
}

// ─── Factory: form em branco (in-memory) ─────────────────────────

/** Cria FormDocument em branco. Persistência via formsApi.create() depois. */
export function createBlankFormDocument(): FormDocument {
  const now = new Date().toISOString();
  return {
    id: uuid(),
    createdAt: now,
    updatedAt: now,
    settings: {
      title: 'Novo Formulário',
      description: '',
      successMessage: 'Obrigado pelo envio!',
      redirectUrl: '',
      submitButtonLabel: 'Enviar',
      webhook: {
        url: '', method: 'POST', headers: [], enabled: false, corsMode: 'no-cors',
        events: { onOpen: false, onSubmit: true, onFieldChange: [] },
      },
      header: { enabled: false, title: '', description: '', imageUrl: '' },
      customHead: '',
      customBody: '',
      paginated: false,
    },
    theme: { ...DEFAULT_THEME },
    fields: [],
  };
}
